import { createTheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { csCZ } from '@mui/material/locale';

// Czech color palette - professional blue/gray scheme
const lightPalette = {
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  background: {
    default: '#fafafa',
    paper: '#ffffff',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
};

const darkPalette = {
  primary: {
    main: '#42a5f5',
    light: '#64b5f6',
    dark: '#1976d2',
    contrastText: '#000000',
  },
  secondary: {
    main: '#ef5350',
    light: '#ff6659',
    dark: '#d32f2f',
    contrastText: '#000000',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#2e7d32',
    contrastText: '#000000',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#ed6c02',
    contrastText: '#000000',
  },
  error: {
    main: '#ef5350',
    light: '#ff6659',
    dark: '#d32f2f',
    contrastText: '#000000',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
  },
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    disabled: 'rgba(255, 255, 255, 0.5)',
  },
};

// Typography configuration with Czech-friendly fonts
const typography = {
  fontFamily: "'Roboto', 'Arial', sans-serif",
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.43,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none' as const,
  },
};

// Responsive breakpoints
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

// Component overrides for accessibility and consistency
const getComponentOverrides = (mode: 'light' | 'dark'): ThemeOptions['components'] => ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '8px 16px',
        '&:focus-visible': {
          outline: `3px solid ${mode === 'light' ? '#1976d2' : '#42a5f5'}`,
          outlineOffset: 2,
        },
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: mode === 'light' 
          ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
          : '0 2px 8px rgba(0, 0, 0, 0.3)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&:focus-within': {
            outline: `2px solid ${mode === 'light' ? '#1976d2' : '#42a5f5'}`,
            outlineOffset: 2,
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: mode === 'light'
          ? '0 1px 3px rgba(0, 0, 0, 0.12)'
          : '0 1px 3px rgba(0, 0, 0, 0.4)',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: 'none',
        boxShadow: mode === 'light'
          ? '2px 0 8px rgba(0, 0, 0, 0.1)'
          : '2px 0 8px rgba(0, 0, 0, 0.3)',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        '&:focus-visible': {
          outline: `2px solid ${mode === 'light' ? '#1976d2' : '#42a5f5'}`,
          outlineOffset: 2,
          borderRadius: 2,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&:focus-visible': {
          outline: `2px solid ${mode === 'light' ? '#1976d2' : '#42a5f5'}`,
          outlineOffset: 2,
        },
      },
    },
  },
});

// Create light theme
export const lightTheme = createTheme(
  {
    palette: {
      mode: 'light',
      ...lightPalette,
    },
    typography,
    breakpoints,
    components: getComponentOverrides('light'),
    shape: {
      borderRadius: 8,
    },
  },
  csCZ
);

// Create dark theme
export const darkTheme = createTheme(
  {
    palette: {
      mode: 'dark',
      ...darkPalette,
    },
    typography,
    breakpoints,
    components: getComponentOverrides('dark'),
    shape: {
      borderRadius: 8,
    },
  },
  csCZ
);
